import React, { useState } from "react";
import { apiRequest } from "src/async/apiUtils";
import Button from "src/components/form/Button";
import Input from "src/components/form/Input";
import Modal from "src/components/shared/Modal";
import WorkspaceSelector from "./WorkspaceSelector";
import { apiUrl } from "src/config/host";
import { encodeRelayState } from "src/helpers/urlBuilder";
import { isValidEmail } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import { ForwardIcon } from "@heroicons/react/24/outline";
import { useSiteSettings } from "src/context/SiteSettingsContext";

export const WorkspaceSpecificLoginForm = ({ lockout, loginDisabled, email, setEmail, password, setPassword, trackLogin, onSubmit }) => {
  const { site } = useSiteSettings();

  const [disabled, setDisabled] = useState(false);
  const [workspaceSelelctModal, setWorkspaceSelelctModal] = useState(false);
  const [userWorkspaces, setUserWorkspaces] = useState([]);

  const getUserWorkspaces = async () => {
    try {
      setDisabled(true);
      const response = await apiRequest("GET", `/portal/users/:email/workspaces`, {
        params: {
          email,
        },
      });
      const workspaces = response?.data?.data;
      if (workspaces?.length === 1 && workspaces[0]?.ssos?.length === 1 && !workspaces[0]?.dual_signin_for_user) {
        window.location.href = `${apiUrl}/login-sso?RelayState=${encodeRelayState({
          origin: window.location.origin,
          sso_id: workspaces[0]?.ssos[0]?._id,
          workspace_id: workspaces[0]?._id,
        })}`;
      } else {
        setUserWorkspaces(workspaces);
        setWorkspaceSelelctModal(true);
      }
    } catch (err) {
      trackLogin(err.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleBackToLogin = () => {
    setWorkspaceSelelctModal(false);
  };

  const renderPasswordOption = ({ showPasswordField = false, showOrOption = true, workspace = null }) => {
    return (
      <div className="flex w-full items-center justify-center">
        <div className="my-5 flex min-w-[350px] flex-col">
          {showOrOption && (
            <div className="my-5 flex items-center justify-between">
              <div className="h-[1px] w-1/3 bg-gray-300"></div>
              <span className="text-xs font-medium text-gray-500 sm:text-sm">OR</span>
              <div className="h-[1px] w-1/3 bg-gray-300"></div>
            </div>
          )}
          {showPasswordField && (
            <div className="w-full">
              <Input
                inline={true}
                labelClasses={"font-medium mb-0"}
                inputValue={password}
                onChange={(e) => setPassword(e.target.value)}
                label={"Password"}
                autoComplete="current-password"
                inputType={"password"}
                inputName={"password"}
                inputClasses={"font-medium"}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onSubmit(e, workspace);
                  }
                }}
              />
            </div>
          )}

          <Button
            buttonClasses={"w-full mt-3"}
            isDisable={lockout || loginDisabled}
            buttonLabelClasses={" !font-normal "}
            buttonLabel={showPasswordField ? "Login" : "Login with password"}
            buttonFunction={(e) => onSubmit(e, workspace)}
            buttonStyles={{
              borderColor: site?.highlight_color,
              backgroundColor: site?.highlight_color,
            }}
          />
          <div className="flex justify-center">
            <Button
              buttonClasses={"!px-0 !h-auto !bg-transparent !rounded-none !text-sm !my-2"}
              buttonHasLink={true}
              buttonLink={"/forgot-password"}
              buttonLabel={"Forgot Password?"}
              buttonLabelClasses={"!text-sm hover:!underline !text-slate-800 !font-medium"}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex w-full flex-col gap-y-2 ">
        <div className="w-full">
          <Input
            inline
            labelClasses={"font-medium mb-0"}
            inputValue={email}
            onChange={(e) => setEmail(e.target.value)}
            label={"Email"}
            autoComplete="username"
            inputType={"email"}
            inputName={"email"}
            inputClasses={"font-medium"}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                getUserWorkspaces();
              }
            }}
          />
        </div>
        <Button
          isDisable={disabled || !isValidEmail(email) || lockout}
          buttonClasses="w-full !gap-1"
          buttonLabelClasses={" !font-normal"}
          buttonHasLink={false}
          buttonLabel={!lockout ? (disabled ? "Proceeding..." : "Proceed") : `Your login has been blocked for ${lockout === 60 ? "01:00" : `00:${lockout < 10 ? "0" : ""}${lockout}`}`}
          buttonFunction={() => {
            getUserWorkspaces();
          }}
          buttonIcon={ForwardIcon}
          buttonIconPosition="right"
          loading={disabled}
          buttonStyles={{
            borderColor: site?.highlight_color,
            backgroundColor: site?.highlight_color,
          }}
        />

        {userWorkspaces?.length > 0 && (
          <Modal
            isOpen={workspaceSelelctModal}
            size="xxl"
            childClasses="!p-0 min-h-[500px] flex flex-col overflow-hidden items-center"
            defaultOptions={{ onCancelButtonVisible: false, onSuccessButtonVisible: false, headerVisible: false }}
            onCancel={() => setWorkspaceSelelctModal(false)}>
            <div className="w-full overflow-y-auto rounded-t-2xl p-6">
              <div className={classNames("mx-auto max-w-4xl", loginDisabled ? "[&_button]:cursor-not-allowed" : "")}>
                <WorkspaceSelector
                  workspaces={userWorkspaces}
                  handleBackToLogin={handleBackToLogin}
                  renderPasswordOption={renderPasswordOption}
                  onSSOSelect={(sso, workspace) => {
                    if (!loginDisabled) {
                      window.location.href = `${apiUrl}/login-sso?RelayState=${encodeRelayState({
                        origin: window.location.origin,
                        sso_id: sso?._id,
                        workspace_id: workspace?._id,
                      })}`;
                    }
                  }}
                />
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};
